// -----------------------------------------------------------------------------
// engine/export/exporter.ts — render the timeline out to a file.
//
// Offline (faster- or slower-than-realtime) render: for each output frame we
//   1. composite it at FULL project resolution on a dedicated WebGL canvas,
//   2. wrap the canvas in a VideoFrame,
//   3. transfer it to the encode worker (which encodes + muxes off-thread).
// Backpressure: we wait for the worker's "ack" before sending the next frame, so
// memory can't run away on a long timeline. At the end we flush + finalize and
// get the finished container bytes back as a Blob.
//
// Tier 0 exports video only; audio mixing/export is Tier 1. (CLAUDE.md §6.)
// -----------------------------------------------------------------------------
import { projectDuration } from '../../core/model';
import type { ProjectStore } from '../../core/store';
import { Emitter } from '../../shared/events';
import type { EncodeRequest, EncodeResponse } from '../../shared/worker-protocol';
import { Compositor } from '../compositor/gl';
import { drawMediaLayer, drawTextLayer } from '../compositor/layer';
import type { MediaManager } from '../media/source';
import { compileRenderPlan } from '../render-plan';
import type { ExportPlan } from './export-path';

export interface ExportProgress {
  phase: 'render' | 'finalize' | 'done' | 'error';
  frame: number;
  totalFrames: number;
  message?: string;
}

export class Exporter {
  readonly events = new Emitter<{ progress: ExportProgress }>();

  private worker?: Worker;
  private awaiting: ((response: EncodeResponse) => void) | null = null;
  private aborted = false;

  /** Request cancellation of an in-progress export (checked each frame). */
  cancel(): void {
    this.aborted = true;
  }

  constructor(
    private readonly store: ProjectStore,
    private readonly media: MediaManager,
  ) {}

  async export(plan: ExportPlan): Promise<Blob> {
    this.aborted = false;
    const project = this.store.state.project;
    const duration = projectDuration(project);
    const totalFrames = Math.max(1, Math.ceil(duration * plan.fps));
    const keyEvery = Math.max(1, Math.round(plan.fps)); // a keyframe ~every second

    // Dedicated full-resolution compositor for export (separate from preview).
    const canvas = new OffscreenCanvas(plan.width, plan.height);
    const compositor = new Compositor(canvas, plan.width, plan.height);

    this.worker = new Worker(new URL('../../workers/encode.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (event: MessageEvent<EncodeResponse>) => {
      const resolve = this.awaiting;
      this.awaiting = null;
      resolve?.(event.data);
    };

    try {
      const started = await this.send({
        type: 'start',
        width: plan.width,
        height: plan.height,
        fps: plan.fps,
        bitrate: plan.bitrate,
        container: plan.container,
        videoCodec: plan.videoCodec,
      });
      if (started.type !== 'started' || !started.ok) {
        throw new Error(started.type === 'started' ? started.error ?? 'encoder failed to start' : 'unexpected');
      }

      for (let i = 0; i < totalFrames; i++) {
        if (this.aborted) {
          this.worker.postMessage({ type: 'cancel' }); // fire-and-forget; finally terminates it
          throw new Error('Export cancelled.');
        }
        const time = i / plan.fps;
        await this.composite(compositor, time);
        compositor.flush();

        const frame = new VideoFrame(canvas, {
          timestamp: Math.round((i * 1_000_000) / plan.fps),
          duration: Math.round(1_000_000 / plan.fps),
        });
        // Transfer the frame; wait for the worker to be ready for the next one.
        await this.send({ type: 'frame', frame, keyFrame: i % keyEvery === 0 }, [frame]);

        this.events.emit('progress', { phase: 'render', frame: i + 1, totalFrames });
      }

      this.events.emit('progress', { phase: 'finalize', frame: totalFrames, totalFrames });
      const done = await this.send({ type: 'finish' });
      if (done.type !== 'done' || !done.ok || !done.buffer) {
        throw new Error(done.type === 'done' ? done.error ?? 'muxing failed' : 'unexpected');
      }

      this.events.emit('progress', { phase: 'done', frame: totalFrames, totalFrames });
      return new Blob([done.buffer], { type: done.mimeType ?? plan.mimeType });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.events.emit('progress', { phase: 'error', frame: 0, totalFrames, message });
      throw err;
    } finally {
      compositor.dispose();
      this.worker.terminate();
      this.worker = undefined;
      this.awaiting = null;
    }
  }

  // Composite one frame at FULL resolution, awaiting each exact source frame.
  private async composite(compositor: Compositor, time: number): Promise<void> {
    const project = this.store.state.project;
    compositor.beginFrame(project.background);
    const textScale = compositor.height / project.height;
    const plan = compileRenderPlan(this.store.state, time);

    for (const layer of plan.layers) {
      if (layer.kind === 'media') {
        const source = this.media.getSource(layer.assetId);
        const bitmap = source ? await source.getFrameExact(layer.sourceTimeSec) : null;
        if (bitmap) drawMediaLayer(compositor, bitmap, layer.opacity);
      } else {
        drawTextLayer(compositor, layer, textScale);
      }
    }
  }

  // Post a message to the encode worker and resolve with its single reply.
  private send(message: EncodeRequest, transfer?: Transferable[]): Promise<EncodeResponse> {
    return new Promise<EncodeResponse>((resolve) => {
      this.awaiting = resolve;
      if (transfer) this.worker!.postMessage(message, transfer);
      else this.worker!.postMessage(message);
    });
  }
}
